import React, { useMemo, useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Clock, DotsSixVertical, Check, User as UserIcon, CaretUp, CaretDown, FunnelSimple, Columns } from '@phosphor-icons/react';
import { WorkTypeIcon } from './KanbanBoard';
import { PriorityIcon, PriorityPill } from './PriorityIcon';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { getDueDateMeta } from '../lib/dueDateColor';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const DEFAULT_STATUSES = [
  { id: 'todo', label: 'To Do', color: '#a1a1aa' },
  { id: 'in_progress', label: 'In Progress', color: '#3b82f6' },
  { id: 'review', label: 'Review', color: '#f59e0b' },
  { id: 'done', label: 'Done', color: '#22c55e' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Stop propagation so clicking an inline editor doesn't open the task sheet.
const stop = (e) => e.stopPropagation();

// ─── Inline editors ──────────────────────────────────────────

function StatusCell({ task, statuses, onTaskUpdate, canEdit }) {
  const [open, setOpen] = useState(false);
  const current = statuses.find(s => s.id === task.status) || { id: task.status, label: task.status, color: '#a1a1aa' };
  if (!canEdit) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-zinc-100 text-zinc-700">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: current.color }} />
        {current.label}
      </span>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={stop}
          data-testid={`list-status-trigger-${task.task_id}`}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: current.color }} />
          {current.label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1" onClick={stop}>
        {statuses.map(s => (
          <button key={s.id} type="button"
            onClick={async (e) => { stop(e); setOpen(false); if (s.id !== task.status) await onTaskUpdate(task.task_id, { status: s.id }); }}
            data-testid={`list-status-option-${task.task_id}-${s.id}`}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-700 hover:bg-zinc-50">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
            <span className="flex-1 text-left">{s.label}</span>
            {task.status === s.id && <Check size={11} className="text-blue-600" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function PriorityCell({ task, onTaskUpdate, canEdit }) {
  const [open, setOpen] = useState(false);
  if (!canEdit) {
    return task.priority ? <PriorityPill priority={task.priority} /> : <span className="text-xs text-slate-300">-</span>;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={stop} data-testid={`list-priority-trigger-${task.task_id}`} className="hover:opacity-70">
          {task.priority ? <PriorityPill priority={task.priority} /> : <span className="text-[11px] text-zinc-400 hover:text-zinc-700">Set priority</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-36 p-1" onClick={stop}>
        {PRIORITIES.map(p => (
          <button key={p.value} type="button"
            onClick={async (e) => { stop(e); setOpen(false); if (p.value !== task.priority) await onTaskUpdate(task.task_id, { priority: p.value }); }}
            data-testid={`list-priority-option-${task.task_id}-${p.value}`}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-700 hover:bg-zinc-50">
            <PriorityIcon priority={p.value} size={12} />
            <span className="flex-1 text-left">{p.label}</span>
            {task.priority === p.value && <Check size={11} className="text-blue-600" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function AssigneeCell({ task, members, getMemberName, getMemberAvatar, onTaskUpdate, canEdit }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = (members || []).filter(m => m.name?.toLowerCase().includes(q.toLowerCase()));
  const current = task.assignee_id;
  const name = current ? getMemberName(current) : null;
  const avatar = current ? getMemberAvatar(current) : null;
  const style = current ? getAvatarStyle(current || name) : null;
  const display = current ? (
    <span className="flex items-center gap-2 text-[12px] text-zinc-700">
      <Avatar className="h-6 w-6"><AvatarImage src={avatar} /><AvatarFallback className={`${style.bg} text-white text-[9px] font-bold`}>{getInitials(name)}</AvatarFallback></Avatar>
      <span className="truncate">{name}</span>
    </span>
  ) : <span className="text-[11px] text-zinc-400">Unassigned</span>;
  if (!canEdit) return display;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={stop} data-testid={`list-assignee-trigger-${task.task_id}`} className="hover:opacity-80 transition-opacity text-left">
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2" onClick={stop}>
        <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search members..." className="h-7 text-[12px] mb-1.5" data-testid={`list-assignee-search-${task.task_id}`} />
        <div className="max-h-52 overflow-y-auto">
          <button type="button"
            onClick={async (e) => { stop(e); setOpen(false); if (current) await onTaskUpdate(task.task_id, { assignee_id: null }); }}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-600 hover:bg-zinc-50">
            <UserIcon size={14} className="text-zinc-400" />
            <span className="flex-1 text-left">Unassigned</span>
            {!current && <Check size={11} className="text-blue-600" />}
          </button>
          {filtered.map(m => {
            const s = getAvatarStyle(m.user_id || m.name);
            return (
              <button key={m.user_id} type="button"
                onClick={async (e) => { stop(e); setOpen(false); if (m.user_id !== current) await onTaskUpdate(task.task_id, { assignee_id: m.user_id }); }}
                data-testid={`list-assignee-option-${task.task_id}-${m.user_id}`}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-zinc-700 hover:bg-zinc-50">
                <Avatar className="h-5 w-5"><AvatarImage src={m.avatar} /><AvatarFallback className={`${s.bg} text-white text-[8px] font-bold`}>{getInitials(m.name)}</AvatarFallback></Avatar>
                <span className="flex-1 text-left truncate">{m.name}</span>
                {m.user_id === current && <Check size={11} className="text-blue-600" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DueDateCell({ task, onTaskUpdate, canEdit }) {
  const [editing, setEditing] = useState(false);
  // Same urgency cues as the Kanban card — a quick scan of the list view
  // surfaces overdue rows immediately. Done tickets stay neutral.
  const meta = task.due_date && task.status !== 'done' ? getDueDateMeta(task.due_date) : null;
  const display = task.due_date ? (
    <span
      className={`inline-flex items-center gap-1 px-1.5 h-[20px] rounded-md text-[11px] whitespace-nowrap ${meta ? meta.className : 'text-zinc-600 ring-1 ring-zinc-200 bg-zinc-50'}`}
      title={meta?.bucket === 'overdue' ? `Overdue · ${meta.label}` : new Date(task.due_date).toLocaleDateString()}
    >
      <Clock size={11} />{meta ? meta.label : new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
    </span>
  ) : null;
  const today = new Date().toISOString().split('T')[0];
  if (!canEdit) return display || <span className="text-xs text-zinc-300">-</span>;
  if (editing) {
    return (
      <Input type="date" autoFocus min={today}
        defaultValue={task.due_date ? task.due_date.slice(0, 10) : ''}
        onClick={stop}
        onBlur={async (e) => {
          setEditing(false);
          const v = e.target.value || null;
          if (v !== (task.due_date ? task.due_date.slice(0, 10) : null)) {
            await onTaskUpdate(task.task_id, { due_date: v });
          }
        }}
        className="h-7 w-32 text-[11px]"
        data-testid={`list-due-date-input-${task.task_id}`}
      />
    );
  }
  return (
    <button onClick={(e) => { stop(e); setEditing(true); }}
      data-testid={`list-due-date-trigger-${task.task_id}`}
      className="hover:opacity-80">
      {display || <span className="text-[11px] text-zinc-400 hover:text-zinc-700">Set date</span>}
    </button>
  );
}

function StoryPointsCell({ task, fieldId, onTaskUpdate, canEdit }) {
  const [editing, setEditing] = useState(false);
  if (!fieldId) return <span className="text-xs text-zinc-300">-</span>;
  const sp = task.custom_fields?.[fieldId];
  if (!canEdit) {
    return sp != null && sp !== '' ? (
      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px] font-bold">{sp}</span>
    ) : <span className="text-xs text-zinc-300">-</span>;
  }
  if (editing) {
    return (
      <Input type="number" min="0" step="1" autoFocus
        defaultValue={sp ?? ''}
        onClick={stop}
        onBlur={async (e) => {
          setEditing(false);
          const raw = e.target.value;
          const next = raw === '' ? '' : Number(raw);
          const prev = sp;
          if (next !== prev) {
            await onTaskUpdate(task.task_id, { custom_fields: { ...(task.custom_fields || {}), [fieldId]: next } });
          }
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditing(false); }}
        className="h-7 w-14 text-[11px]"
        data-testid={`list-sp-input-${task.task_id}`}
      />
    );
  }
  return (
    <button onClick={(e) => { stop(e); setEditing(true); }}
      data-testid={`list-sp-trigger-${task.task_id}`}
      className="hover:opacity-70">
      {sp != null && sp !== '' ? (
        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px] font-bold">{sp}</span>
      ) : <span className="text-[11px] text-zinc-400 hover:text-zinc-700">—</span>}
    </button>
  );
}

// ─── Column registry + helpers ────────────────────────────────

const PRIORITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };
const WORK_TYPES = [{ value: 'task', label: 'Task' }, { value: 'story', label: 'Story' }, { value: 'bug', label: 'Bug' }];

function fmtDate(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; }
}

// Build the full column catalogue for a project (built-ins + custom fields).
function buildColumns(customFields, storyPointsFieldId) {
  const cols = [
    { id: 'type', label: 'Type', width: 'w-16', align: 'center', sortable: true, filter: 'enum' },
    { id: 'key', label: 'Key', width: 'w-20', sortable: true },
    { id: 'title', label: 'Title', always: true, sortable: true },
    { id: 'sp', label: 'SP', width: 'w-16', align: 'center', sortable: true },
    { id: 'status', label: 'Status', width: 'w-32', sortable: true, filter: 'enum' },
    { id: 'priority', label: 'Priority', width: 'w-24', sortable: true, filter: 'enum' },
    { id: 'assignee', label: 'Assignee', width: 'w-44', sortable: true, filter: 'enum' },
    { id: 'reporter', label: 'Reporter', width: 'w-40', sortable: true },
    { id: 'due_date', label: 'Due Date', width: 'w-28', sortable: true },
    { id: 'tags', label: 'Tags', width: 'w-40' },
    { id: 'created', label: 'Created', width: 'w-28', sortable: true },
    { id: 'updated', label: 'Updated', width: 'w-28', sortable: true },
  ];
  (customFields || []).forEach(cf => {
    if (cf.field_id === storyPointsFieldId) return; // SP already has a dedicated column
    cols.push({ id: cf.field_id, label: cf.name, width: 'w-36', sortable: true, custom: cf });
  });
  return cols;
}

const DEFAULT_VISIBLE = ['type', 'key', 'title', 'sp', 'status', 'priority', 'assignee', 'due_date'];

// ─── Main list ────────────────────────────────────────────────

export default function TaskListView({
  tasks, onTaskClick, getMemberName, getMemberAvatar, onReorder,
  onTaskUpdate, members = [], customFields = [], customStatuses = [], canEdit = true,
  selectedIds, onToggleSelect, onToggleSelectAll, activeTaskId, projectId = 'default',
}) {
  const allStatuses = useMemo(() => [
    ...DEFAULT_STATUSES,
    ...((customStatuses || []).map(s => ({ id: s.id, label: s.label, color: s.color || '#6b7280' }))),
  ], [customStatuses]);

  const storyPointsFieldId = useMemo(() => {
    const f = (customFields || []).find(x => /story\s*points?/i.test(x.name || ''));
    return f?.field_id || null;
  }, [customFields]);

  const columns = useMemo(() => buildColumns(customFields, storyPointsFieldId), [customFields, storyPointsFieldId]);
  const storageKey = `iw_list_cols_${projectId}`;

  // Visible columns — persisted per project in localStorage.
  const [visible, setVisible] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {}
    return DEFAULT_VISIBLE;
  });
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      setVisible(Array.isArray(saved) && saved.length ? saved : DEFAULT_VISIBLE);
    } catch { setVisible(DEFAULT_VISIBLE); }
  }, [storageKey]);
  const persist = (next) => { setVisible(next); try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {} };
  const toggleCol = (id) => {
    if (id === 'title') return; // title is mandatory
    persist(visible.includes(id) ? visible.filter(x => x !== id) : [...visible, id]);
  };

  const visibleColumns = useMemo(() => {
    // Keep the catalogue order; title always present.
    const set = new Set(visible.includes('title') ? visible : ['title', ...visible]);
    return columns.filter(c => set.has(c.id));
  }, [columns, visible]);

  // Sorting.
  const [sort, setSort] = useState({ col: null, dir: 'asc' });
  const cycleSort = (colId) => {
    setSort(prev => {
      if (prev.col !== colId) return { col: colId, dir: 'asc' };
      if (prev.dir === 'asc') return { col: colId, dir: 'desc' };
      return { col: null, dir: 'asc' };
    });
  };

  // Per-column filters (enum columns only): { status:[], priority:[], assignee:[], type:[] }
  const [colFilters, setColFilters] = useState({});
  const toggleColFilter = (colId, val) => {
    setColFilters(prev => {
      const cur = prev[colId] || [];
      const next = cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val];
      return { ...prev, [colId]: next };
    });
  };
  const clearColFilter = (colId) => setColFilters(prev => ({ ...prev, [colId]: [] }));

  const enumOptions = (colId) => {
    if (colId === 'status') return allStatuses.map(s => ({ value: s.id, label: s.label }));
    if (colId === 'priority') return PRIORITIES;
    if (colId === 'type') return WORK_TYPES;
    if (colId === 'assignee') return [{ value: '__none__', label: 'Unassigned' }, ...members.map(m => ({ value: m.user_id, label: m.name || m.email }))];
    return [];
  };

  const matchColFilter = (task) => Object.entries(colFilters).every(([colId, vals]) => {
    if (!vals || !vals.length) return true;
    if (colId === 'status') return vals.includes(task.status);
    if (colId === 'priority') return vals.includes(task.priority);
    if (colId === 'type') return vals.includes(task.work_type || 'task');
    if (colId === 'assignee') return vals.includes(task.assignee_id || '__none__');
    return true;
  });

  const sortValue = (task, colId) => {
    switch (colId) {
      case 'type': return task.work_type || 'task';
      case 'key': return task.task_key || '';
      case 'title': return (task.title || '').toLowerCase();
      case 'sp': { const v = task.custom_fields?.[storyPointsFieldId]; return v == null || v === '' ? -Infinity : Number(v); }
      case 'status': { const i = allStatuses.findIndex(s => s.id === task.status); return i < 0 ? Infinity : i; }
      case 'priority': return PRIORITY_RANK[task.priority] || 0;
      case 'assignee': return (task.assignee_id ? (getMemberName(task.assignee_id) || '') : '').toLowerCase();
      case 'reporter': return (task.reporter_id ? (getMemberName(task.reporter_id) || '') : '').toLowerCase();
      case 'due_date': return task.due_date ? Date.parse(task.due_date) : Infinity;
      case 'created': return task.created_at ? Date.parse(task.created_at) : 0;
      case 'updated': return Date.parse(task.last_activity_at || task.updated_at || task.created_at || 0);
      case 'tags': return (task.tags || []).join(',').toLowerCase();
      default: { const v = task.custom_fields?.[colId]; return v == null ? '' : (typeof v === 'number' ? v : String(v).toLowerCase()); }
    }
  };

  const displayTasks = useMemo(() => {
    let arr = tasks.filter(matchColFilter);
    if (sort.col) {
      arr = [...arr].sort((a, b) => {
        const va = sortValue(a, sort.col), vb = sortValue(b, sort.col);
        let cmp = 0;
        if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
        else cmp = String(va).localeCompare(String(vb));
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return arr;
  }, [tasks, colFilters, sort, storyPointsFieldId, allStatuses]);

  const anyColFilter = Object.values(colFilters).some(v => v && v.length);
  const dragEnabled = canEdit && !sort.col && !anyColFilter && !!onReorder;

  const handleDragEnd = (result) => {
    if (!result.destination || !onReorder) return;
    const items = Array.from(displayTasks);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    onReorder(items.map(t => t.task_id));
  };

  const update = onTaskUpdate || (async () => {});
  const selectionEnabled = !!onToggleSelect && !!onToggleSelectAll;
  const selectedSet = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  const allSelected = selectionEnabled && displayTasks.length > 0 && displayTasks.every(t => selectedSet.has(t.task_id));
  const someSelected = selectionEnabled && displayTasks.some(t => selectedSet.has(t.task_id));
  const colSpan = visibleColumns.length + 2 + (selectionEnabled ? 1 : 0);

  // Render a single body cell for a column.
  const renderCell = (col, task) => {
    switch (col.id) {
      case 'type': return <WorkTypeIcon type={task.work_type || 'task'} size={14} />;
      case 'key': return <span className="text-xs font-mono text-zinc-400">{task.task_key}</span>;
      case 'title': return <span className="text-sm font-medium text-zinc-900">{task.title}</span>;
      case 'sp': return <StoryPointsCell task={task} fieldId={storyPointsFieldId} onTaskUpdate={update} canEdit={canEdit} />;
      case 'status': return <StatusCell task={task} statuses={allStatuses} onTaskUpdate={update} canEdit={canEdit} />;
      case 'priority': return <PriorityCell task={task} onTaskUpdate={update} canEdit={canEdit} />;
      case 'assignee': return <AssigneeCell task={task} members={members} getMemberName={getMemberName} getMemberAvatar={getMemberAvatar} onTaskUpdate={update} canEdit={canEdit} />;
      case 'reporter': {
        if (!task.reporter_id) return <span className="text-[11px] text-zinc-400">—</span>;
        const name = getMemberName(task.reporter_id); const s = getAvatarStyle(task.reporter_id);
        return <span className="flex items-center gap-2 text-[12px] text-zinc-700"><Avatar className="h-5 w-5"><AvatarImage src={getMemberAvatar(task.reporter_id)} /><AvatarFallback className={`${s.bg} text-white text-[8px] font-bold`}>{getInitials(name)}</AvatarFallback></Avatar><span className="truncate">{name}</span></span>;
      }
      case 'due_date': return <DueDateCell task={task} onTaskUpdate={update} canEdit={canEdit} />;
      case 'tags': return (task.tags || []).length ? <div className="flex flex-wrap gap-1">{task.tags.slice(0, 4).map(t => <span key={t} className="px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-medium">{t}</span>)}</div> : <span className="text-[11px] text-zinc-300">—</span>;
      case 'created': return <span className="text-[11px] text-zinc-500">{fmtDate(task.created_at)}</span>;
      case 'updated': return <span className="text-[11px] text-zinc-500">{fmtDate(task.last_activity_at || task.updated_at)}</span>;
      default: {
        const cf = col.custom; const v = task.custom_fields?.[col.id];
        if (v == null || v === '') return <span className="text-[11px] text-zinc-300">—</span>;
        if (cf?.type === 'date') return <span className="text-[11px] text-zinc-500">{fmtDate(v)}</span>;
        const text = String(v).replace(/<[^>]*>/g, ' ').trim();
        return <span className="text-[12px] text-zinc-600 truncate block max-w-[220px]" title={text}>{text}</span>;
      }
    }
  };

  return (
    <div className="h-full overflow-auto p-6 bg-slate-50/60" data-testid="task-list-view">
      <div className="flex items-center justify-end mb-2">
        <ColumnChooser columns={columns} visible={visible} onToggle={toggleCol} onReset={() => persist(DEFAULT_VISIBLE)} />
      </div>
      <div className="bg-white rounded-xl ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-200 bg-slate-50 hover:bg-slate-50">
              {selectionEnabled && (
                <TableHead className="w-8 text-center">
                  <input type="checkbox" checked={allSelected}
                    ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                    onChange={(e) => onToggleSelectAll(e.target.checked, displayTasks.map(t => t.task_id))}
                    className="rounded border-zinc-300 cursor-pointer accent-blue-600" data-testid="list-select-all"
                    onClick={(e) => e.stopPropagation()} />
                </TableHead>
              )}
              <TableHead className="w-8"></TableHead>
              {visibleColumns.map(col => (
                <ColumnHeader
                  key={col.id} col={col} sort={sort} onSort={cycleSort}
                  filterVals={colFilters[col.id] || []} enumOptions={col.filter === 'enum' ? enumOptions(col.id) : null}
                  onToggleFilter={toggleColFilter} onClearFilter={clearColFilter}
                />
              ))}
            </TableRow>
          </TableHeader>
          <Droppable droppableId="task-list">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {displayTasks.length === 0 && (
                  <TableRow><TableCell colSpan={colSpan} className="text-center text-sm text-zinc-400 py-12">No tasks found.</TableCell></TableRow>
                )}
                {displayTasks.map((task, index) => {
                  const isSelected = selectionEnabled && selectedSet.has(task.task_id);
                  const isActive = activeTaskId === task.task_id;
                  return (
                  <Draggable key={task.task_id} draggableId={task.task_id} index={index} isDragDisabled={!dragEnabled}>
                    {(provided, snapshot) => (
                      <TableRow ref={provided.innerRef} {...provided.draggableProps}
                        data-testid={`task-row-${task.task_id}`} data-active={isActive ? 'true' : 'false'}
                        onClick={() => onTaskClick(task)}
                        className={`cursor-pointer border-b border-slate-100 transition-colors duration-150
                          ${snapshot.isDragging ? 'bg-white shadow-lg ring-2 ring-blue-300'
                            : isActive ? 'bg-blue-50/70 hover:bg-blue-50 shadow-[inset_3px_0_0_0_rgb(59,130,246)]'
                              : isSelected ? 'bg-blue-50/40 hover:bg-blue-50/70' : 'hover:bg-slate-100/80'}`}
                      >
                        {selectionEnabled && (
                          <TableCell className="w-8 text-center" onClick={stop}>
                            <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(task.task_id)} onClick={stop}
                              className="rounded border-zinc-300 cursor-pointer accent-blue-600" data-testid={`list-select-${task.task_id}`} />
                          </TableCell>
                        )}
                        <TableCell {...(dragEnabled ? provided.dragHandleProps : {})} className={`w-8 text-center ${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                          <DotsSixVertical size={14} className={`mx-auto transition-colors ${dragEnabled ? 'text-zinc-300 hover:text-zinc-500' : 'text-zinc-200'}`} />
                        </TableCell>
                        {visibleColumns.map(col => (
                          <TableCell key={col.id} className={col.align === 'center' ? 'text-center' : ''}>{renderCell(col, task)}</TableCell>
                        ))}
                      </TableRow>
                    )}
                  </Draggable>
                  );
                })}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>
      </div>
    </div>
  );
}

// Header cell: label + sort toggle + optional enum filter funnel.
function ColumnHeader({ col, sort, onSort, filterVals, enumOptions, onToggleFilter, onClearFilter }) {
  const [open, setOpen] = useState(false);
  const isSorted = sort.col === col.id;
  const active = filterVals.length > 0;
  return (
    <TableHead className={`text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold ${col.width || ''} ${col.align === 'center' ? 'text-center' : ''}`}>
      <div className={`flex items-center gap-1 ${col.align === 'center' ? 'justify-center' : ''}`}>
        <button type="button" onClick={() => col.sortable && onSort(col.id)}
          className={`inline-flex items-center gap-1 ${col.sortable ? 'hover:text-slate-800 cursor-pointer' : 'cursor-default'}`}
          data-testid={`list-col-sort-${col.id}`}>
          {col.label}
          {col.sortable && (
            <CaretUpDown isSorted={isSorted} dir={sort.dir} />
          )}
        </button>
        {enumOptions && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button type="button" onClick={stop} data-testid={`list-col-filter-${col.id}`}
                className={`p-0.5 rounded hover:bg-slate-200 ${active ? 'text-blue-600' : 'text-slate-400'}`}>
                <FunnelSimple size={12} weight={active ? 'fill' : 'regular'} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-52 p-1" onClick={stop}>
              <div className="max-h-64 overflow-y-auto">
                {enumOptions.map(o => (
                  <button key={o.value} type="button" onClick={() => onToggleFilter(col.id, o.value)}
                    data-testid={`list-col-filter-${col.id}-${o.value}`}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12px] hover:bg-zinc-50 normal-case tracking-normal ${filterVals.includes(o.value) ? 'bg-blue-50/60' : ''}`}>
                    <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border flex-shrink-0 ${filterVals.includes(o.value) ? 'bg-blue-600 border-blue-600 text-white' : 'border-zinc-300'}`}>
                      {filterVals.includes(o.value) && <Check size={9} weight="bold" />}
                    </span>
                    <span className="flex-1 text-left text-zinc-700 truncate">{o.label}</span>
                  </button>
                ))}
              </div>
              {active && (
                <button type="button" onClick={() => { onClearFilter(col.id); }} className="w-full text-[11px] text-slate-500 hover:text-slate-800 py-1.5 border-t border-zinc-100 mt-1 normal-case tracking-normal">Clear</button>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </TableHead>
  );
}

function CaretUpDown({ isSorted, dir }) {
  return (
    <span className="inline-flex flex-col leading-none -space-y-1">
      <CaretUp size={8} weight="fill" className={isSorted && dir === 'asc' ? 'text-blue-600' : 'text-slate-300'} />
      <CaretDown size={8} weight="fill" className={isSorted && dir === 'desc' ? 'text-blue-600' : 'text-slate-300'} />
    </span>
  );
}

// Column visibility chooser.
function ColumnChooser({ columns, visible, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="list-columns-btn"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 shadow-sm">
          <Columns size={14} /> Columns
          <span className="text-slate-400">({visible.length})</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50/60 flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-500">Columns</span>
          <button type="button" onClick={onReset} className="text-[11px] text-blue-600 hover:text-blue-700" data-testid="list-columns-reset">Reset</button>
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {columns.map(col => {
            const checked = visible.includes(col.id) || col.id === 'title';
            const locked = col.id === 'title';
            return (
              <button key={col.id} type="button" onClick={() => onToggle(col.id)} disabled={locked}
                data-testid={`list-column-toggle-${col.id}`}
                className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors ${locked ? 'opacity-60 cursor-default' : 'hover:bg-zinc-50'}`}>
                <span className={`inline-flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 ${checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-zinc-300'}`}>
                  {checked && <Check size={10} weight="bold" />}
                </span>
                <span className="flex-1 text-[13px] text-zinc-700 truncate">{col.label}{col.custom && <span className="ml-1 text-[9px] text-zinc-400 uppercase">field</span>}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
